import React, { useState } from 'react';
import { Accordion, Button, Form } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DualRange from '~/components/filter/DualRange';
import { FilterDefinition } from '~/types/Filter';
import { useFormatPrice } from '~/utils/price';

const VISIBLE_BUCKETS = 10;
/** Slider granularity in minor units — 10 Kč, so dragging never lands on hellers. */
const PRICE_STEP = 1000;

interface Props {
  filter: FilterDefinition;
}

const FilterAccordionItem = ({ filter }: Props) => {
  const { t } = useTranslation();
  const methods = useFormContext();
  const formatPrice = useFormatPrice();
  const [expanded, setExpanded] = useState(false);

  if (!methods) {
    throw new Error('`FilterAccordionItem` must be used within a `FormProvider` from `react-hook-form`.');
  }

  const { register, getValues, setValue } = methods;
  const rangeValue = 'min' in filter ? (getValues(filter.key) as [number, number] | undefined) : undefined;
  const sliderMin = 'min' in filter ? Math.floor(filter.min / PRICE_STEP) * PRICE_STEP : 0;
  const sliderMax = 'min' in filter ? Math.ceil(filter.max / PRICE_STEP) * PRICE_STEP : 0;

  const buckets = 'buckets' in filter ? filter.buckets : [];
  const selectedValues = 'buckets' in filter ? ((getValues(filter.key) as string[] | undefined) ?? []) : [];
  const hasHiddenSelected = buckets.slice(VISIBLE_BUCKETS).some((option) => selectedValues.includes(option.key));
  const showAll = expanded || hasHiddenSelected;
  const visibleBuckets = showAll ? buckets : buckets.slice(0, VISIBLE_BUCKETS);

  return (
    <Accordion.Item eventKey={filter.key} className="filter-item">
      <fieldset key={filter.key}>
        <legend className="visually-hidden">{filter.label}</legend>
        <Accordion.Header as="div" className="filter-item__header">
          {filter.label}
        </Accordion.Header>
        <Accordion.Body className="filter-item__body">
          {'buckets' in filter && (
            <>
              {visibleBuckets.map((option) => (
                <Form.Check
                  type="checkbox"
                  id={`${filter.key}-${option.key}`}
                  className="filter-item__checkbox"
                  key={option.key}
                >
                  <Form.Check.Input
                    type="checkbox"
                    value={option.key}
                    {...register(filter.key, {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        e.target.blur();
                      },
                    })}
                    disabled={option.count === 0}
                  />
                  <Form.Check.Label>{option.label}</Form.Check.Label>
                  <span className="filter-item__number">{option.count}</span>
                </Form.Check>
              ))}
              {buckets.length > VISIBLE_BUCKETS && !hasHiddenSelected && (
                <Button
                  type="button"
                  variant="link"
                  className="filter-item__toggle"
                  onClick={() => setExpanded((prev) => !prev)}
                >
                  {expanded
                    ? t('components.filter.show_less')
                    : t('components.filter.show_more', { count: buckets.length - VISIBLE_BUCKETS })}
                </Button>
              )}
            </>
          )}
          {'min' in filter && (
            <>
              {filter.key === 'price' && (
                <p className="filter-item__price-info">
                  {t('components.filter.highest_price', {
                    price: formatPrice(filter.max, filter.currency),
                  })}
                </p>
              )}

              {/* Bounds are widened onto the step grid: a range input can only reach
                  values that sit on it, so an unrounded max would be unselectable and
                  the priciest product would drop out of the results. The untouched
                  default sits on the grid too, otherwise the first drag jumps. */}
              <DualRange
                name={filter.key}
                min={sliderMin}
                max={sliderMax}
                step={PRICE_STEP}
                value={rangeValue ?? [sliderMin, sliderMax]}
                currency={filter.currency}
                divisor={100}
                onChange={(next) => {
                  setValue(filter.key, next, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
            </>
          )}
        </Accordion.Body>
      </fieldset>
    </Accordion.Item>
  );
};

export default FilterAccordionItem;
